import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  AxiosPropertyInstance,
  AxiosPaymentInstance,
} from "../../../common/utils/AxiosInstance";
import { CheckApiRateLimitError } from "../../../common/utils/functions/helperFunctions";
// Add-property beyond the free limit costs credits — every create surface goes
// through this thunk, so the "confirm before charging" popup + top-up live here.
import {
  confirmCreditCharge,
  isInsufficientCredits,
} from "../../../common/utils/creditCharge";
import { confirmCreditChargeModal } from "../../../common/utils/creditChargeModal";
import { promptTopUp } from "../../../pages/intelligence/v2/lib/paywall";
import { refreshCustomerCredits } from "../../../common/utils/refreshCustomerCredits";
import {
  notifyShortfallResult,
  payShortfall,
  splitPrice,
} from "../../../common/utils/payShortfall";

export const getPropertyCategoryReference = createAsyncThunk(
  "getPropertyCategoryReference",
  async (_, { rejectWithValue }) => {
    const URL = "/categories";
    try {
      const res = await AxiosPropertyInstance.get(URL);
      //
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const step1CreateProperty = createAsyncThunk(
  "step1CreateProperty",
  async (data, { rejectWithValue, dispatch, getState }) => {
    const token = JSON.parse(localStorage.getItem("token"));
    const post = (body) =>
      AxiosPropertyInstance.post("/", body, {
        headers: { "x-auth-token": token },
      });
    try {
      const res = await post(data);
      // A confirmed charge went through on this direct post (e.g. the auto-resume
      // after a top-up, which sends confirmCharge up front) — refresh the wallet so
      // the navbar shows the deduction live instead of the pre-charge balance.
      if (data && data.confirmCharge) refreshCustomerCredits(dispatch);
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const status = error?.response?.status || 500;

      // 402 = adding this property crosses the free limit and needs the user to
      // CONFIRM the credits charge. Show the popup (or top-up modal), then retry
      // once with confirmCharge. `!data.confirmCharge` prevents any loop — a
      // caller that already confirmed never lands here. This lives in the thunk
      // so EVERY add-property surface gets the popup, not just one screen.
      if (status === 402 && !(data && data.confirmCharge)) {
        const balance = Number(getState()?.customer?.credits) || 0;
        const { proceed, needTopUp, cost, payRest } = await confirmPropertyAddCharge(
          dispatch,
          error,
          balance
        );
        if (!proceed) {
          // `needTopUp` tells the caller the add stalled on insufficient credits
          // (top-up modal is open) so it can auto-resume once credits are added,
          // vs. a plain user cancel. `cost` lets it know the target balance.
          return rejectWithValue({
            status,
            data: error?.response?.data || {},
            cancelled: !needTopUp,
            needTopUp: !!needTopUp,
            cost: cost || 0,
          });
        }
        try {
          const res2 = await post({
            ...data,
            confirmCharge: true,
            ...(payRest ? { payShortfall: true } : {}),
          });
          const checkout = res2?.data?.data?.item;
          if (checkout?.sessionId) {
            // Credits were short: pay the rest; the property is created once
            // paid, under the id the backend reserved for it.
            const payStatus = await payShortfall(checkout);
            refreshCustomerCredits(dispatch);
            notifyShortfallResult({ status: payStatus });
            if (payStatus !== "PAID") {
              return rejectWithValue({
                status: 402,
                data: res2?.data || {},
                cancelled: true,
              });
            }
            res2.data.data.item = { id: checkout.propertyId };
          }
          refreshCustomerCredits(dispatch); // keep the wallet in step after charging
          return { response: JSON.parse(JSON.stringify(res2)) };
        } catch (error2) {
          if (isInsufficientCredits(error2)) promptTopUp(dispatch);
          return rejectWithValue({
            status: error2?.response?.status || 500,
            data: error2?.response?.data || { message: "Unknown error" },
          });
        }
      }

      return rejectWithValue({
        status,
        data: error?.response?.data || { message: "Unknown error" },
      });
    }
  }
);

/**
 * Show the "reached your free limit — this costs N credits" popup for a property
 * add that the backend refused with 402. Uses the live quote when available (so
 * the popup shows the real balance + routes to top-up when short); otherwise it
 * falls back to the cost parsed from the 402 message.
 *
 * @returns {Promise<{proceed: boolean, needTopUp?: boolean, cost: number}>}
 *   proceed  — retry the add with confirmCharge
 *   needTopUp — proceed was declined because the balance is short (top-up modal
 *               is now open); the caller can auto-resume once credits are added
 *   cost     — credits the add will deduct
 */
async function confirmPropertyAddCharge(dispatch, error, balance = 0) {
  let quote = null;
  try {
    quote = await getPropertyAddQuote();
  } catch {
    /* fall back to the message-parsed cost below */
  }

  // The quote is authoritative (fresh limit/price + real balance from the server).
  if (quote) {
    const cost = Number(quote.cost) || 0;
    // If it now says the add is free — e.g. the admin just raised the limit above
    // the user's count — proceed and IGNORE the stale 402. No popup, no charge.
    if (!quote.willCharge || cost <= 0) return { proceed: true, cost: 0 };
    const bal = Number(quote.balance) || 0;
    const enough = quote.enough && bal >= cost;
    if (!enough) {
      // Short: offer to use the credits there are and pay the rest by Cashfree.
      const proceed = await confirmCreditChargeModal({
        cost,
        balance: bal,
        freeLimit: Number(quote.freeLimit) || 0,
        unit: "property",
        label: "Adding this property",
        shortfall: splitPrice(cost, bal),
      });
      return { proceed, payRest: proceed, cost };
    }
    const proceed = await confirmCreditCharge(dispatch, quote, {
      unit: "property",
      label: "Adding this property",
    });
    return { proceed, cost };
  }

  // Quote request failed — fall back to the cost parsed from the 402 message, and
  // use the real wallet balance (not a hardcoded 0) so the popup isn't misleading.
  const msg = error?.response?.data?.status?.message || "";
  const m = msg.match(/(\d+)\s*credit/i);
  const cost = m ? Number(m[1]) : 0;
  if (!cost) return { proceed: true, cost: 0 }; // unknown cost — let the retry proceed
  const bal = Number(balance) || 0;
  if (bal < cost) {
    // Short: offer to use the credits there are and pay the rest by Cashfree.
    const proceed = await confirmCreditChargeModal({
      cost,
      balance: bal,
      unit: "property",
      label: "Adding this property",
      shortfall: splitPrice(cost, bal),
    });
    return { proceed, payRest: proceed, cost };
  }
  const proceed = await confirmCreditChargeModal({
    cost,
    balance: bal,
    unit: "property",
    label: "Adding this property",
  });
  return { proceed, cost };
}

/** Quote the credits cost of adding the next property (free limit + price are
 *  admin-configured). Returns
 *  { willCharge, cost, chargedCount, balance, enough, freeLimit, price, currentCount }
 *  so the Add-property flow can CONFIRM the charge before creating. Plain call
 *  (not a thunk) — used inline in the submit handler. */
export async function getPropertyAddQuote() {
  const token = JSON.parse(localStorage.getItem("token"));
  const res = await AxiosPropertyInstance.get("/add-quote", {
    headers: { "x-auth-token": token },
  });
  return res?.data?.data?.item || null;
}

// const CheckApiRateLimitError = (statusCode, error) => {

//    if (statusCode === 429) {
//       return { data: error.response.data, status: 429 }

//    } else {
//       return { response: JSON.parse(JSON.stringify(error)) }
//    }
// }

export const UpdateAddProperty = createAsyncThunk(
  "UpdateAddProperty",
  async (data, { rejectWithValue }) => {
    const id = data.id;
    delete data.id;
    delete data.state;
    // data.demand = "medium";
    const URL = `/${id}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPropertyInstance.put(URL, data, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );
      return rejectWithValue({ ...returnedError });
    }
  }
);

export const addtoListing = createAsyncThunk(
  "addtoListing",
  async (data, { rejectWithValue }) => {
    const id = data._id;
    delete data._id;
    delete data.state;
    data.demand = "medium";
    const URL = `/${id}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPropertyInstance.put(URL, data, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );
      return rejectWithValue({ ...returnedError });
    }
  }
);

export const getPropertyData = createAsyncThunk(
  "getPropertyData",
  async (id, { rejectWithValue }) => {
    const URL = `/${id}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPropertyInstance.get(URL, {
        headers: { "x-auth-token": token },
      });
      return { id, response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const status = error?.response?.status || 500;
      const errorData = error?.response?.data || { message: "Unknown error" };

      return rejectWithValue({
        status,
        data: errorData,
      });
      // return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getPropertyOnlyDocsData = createAsyncThunk(
  "getPropertyOnlyDocsData",
  async (id, { rejectWithValue }) => {
    const URL = `/${id}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPropertyInstance.get(URL, {
        headers: { "x-auth-token": token },
      });

      return {
        propertyId: id,
        response: JSON.parse(JSON.stringify(res)),
      };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getPropertyMarketValue = createAsyncThunk(
  "getPropertyMarketValue",
  async (_, { rejectWithValue }) => {
    const URL = "/totalMarketValue";
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPropertyInstance.get(URL, {
        headers: { "x-auth-token": token },
      });

      return {
        response: JSON.parse(JSON.stringify(res)),
      };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const EditProperty = createAsyncThunk(
  "EditProperty",
  async (data, { rejectWithValue }) => {
    const id = data._id;
    delete data._id;
    const URL = `/${id}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPropertyInstance.put(URL, data.response, {
        headers: { "x-auth-token": token },
      });

      return {
        propertyId: id,
        response: JSON.parse(JSON.stringify(res)),
      };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const generatePropertyDescription = createAsyncThunk(
  "generatePropertyDescription",
  async (data, { rejectWithValue }) => {
    const URL = `/description`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });
      return {
        response: JSON.parse(JSON.stringify(res)),
      };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const deleteProperty = createAsyncThunk(
  "deleteProperty",
  async (data, { rejectWithValue }) => {
    const id = data._id;

    delete data._id;
    const URL = `/${id}/remove`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPropertyInstance.delete(URL, {
        headers: { "x-auth-token": token },
      });

      return {
        propertyId: id,
        response: JSON.parse(JSON.stringify(res)),
      };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const transferProperty = createAsyncThunk(
  "transferProperty",
  async (data, { rejectWithValue }) => {
    const URL = `transferProperty`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response?.data)) };
    } catch (error) {
      return rejectWithValue(
        JSON.parse(JSON.stringify(error?.response?.data?.status))
      );
    }
  }
);

export const getPropertyOnlyImagesData = createAsyncThunk(
  "getPropertyOnlyImagesData",
  async (id, { rejectWithValue }) => {
    const URL = `/${id}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPropertyInstance.get(URL, {
        headers: { "x-auth-token": token },
      });

      return {
        propertyId: id,
        response: JSON.parse(JSON.stringify(res)),
      };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getPropertyOnlyVideoURLData = createAsyncThunk(
  "getPropertyOnlyVideoURLData",
  async (id, { rejectWithValue }) => {
    const URL = `/${id}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPropertyInstance.get(URL, {
        headers: { "x-auth-token": token },
      });

      return {
        propertyId: id,
        response: JSON.parse(JSON.stringify(res)),
      };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const PostPropertyDocsUpload = createAsyncThunk(
  "PostPropertyDocsUpload",
  async ({ propertyId, documentId, data }, { rejectWithValue }) => {
    // FIX: backend route is `/docs/:docId` (was the non-existent `/document/:docId`).
    const URL = `/${propertyId}/docs/${documentId}`;

    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.put(URL, data, {
        headers: { "x-auth-token": token },
      });

      return {
        propertyId: propertyId,
        response: JSON.parse(JSON.stringify(response)),
      };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const deletePropertyDocs = createAsyncThunk(
  "deletePropertDocs",
  async ({ propertyId, documentId, data }, { rejectWithValue }) => {
    const URL = `/${propertyId}/document/${documentId}`;
    const token = JSON.parse(localStorage.getItem("token"));

    try {
      const res = await AxiosPropertyInstance.delete(URL, {
        headers: { "x-auth-token": token },
        data,
      });

      return {
        propertyId: data?.propertyId,
        response: JSON.parse(JSON.stringify(res)),
      };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const uploadImages = createAsyncThunk(
  "uploadImage",
  async ({ propertyId, data }, { rejectWithValue }) => {
    const URL = `/${propertyId}/photo`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });

      return {
        propertyId: propertyId,
        response: JSON.parse(JSON.stringify(res)),
      };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const deletePropertyImages = createAsyncThunk(
  "deletePropertyImages",
  async (data, { rejectWithValue }) => {
    const propertyId = data?.propertyId;
    delete data?.propertyId;
    const URL = `/${propertyId}/photo`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.delete(URL, {
        headers: { "x-auth-token": token },
        data: data,
      });

      return {
        propertyId: data?.propertyId,
        response: JSON.parse(JSON.stringify(response)),
      };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getPublicSingleProperty = createAsyncThunk(
  "getPublicSingleProperty",
  async (id, { rejectWithValue }) => {
    const URL = `/${id}/public`;
    // /public serves only verified listings to everyone else; send the token
    // (when signed in) so the owner can still open their own unlisted property
    // from the map. No token → no header, so the signed-out guard is untouched.
    const token = JSON.parse(localStorage.getItem("token") || "null");
    try {
      const res = await AxiosPropertyInstance.get(
        URL,
        token ? { headers: { "x-auth-token": token } } : undefined
      );

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const shareDocument = createAsyncThunk(
  "shareDocument",
  async ({ propertyId, documentId, fileID }, { rejectWithValue }) => {
    // FIX: backend route is `/docs/:docId/file/:fileId/share` (was non-existent `/document/...`).
    const URL = `/${propertyId}/docs/${documentId}/file/${fileID}/share`;

    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.get(URL, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getLakesAndPonds = createAsyncThunk(
  "getLakesAndPonds",
  async (_, { rejectWithValue }) => {
    const URL = "/static/default/lakes.json";
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.get(URL, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getPropertiesMap = createAsyncThunk(
  "getPropertiesMap",
  async (data, { rejectWithValue }) => {
    const URL = "/search/coordinates";
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getBuyAndRentPropertiesMap = createAsyncThunk(
  "getBuyAndRentPropertiesMap",
  async (data, { rejectWithValue }) => {
    const URL = "/search/coordinates";
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data);
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getBuyWishlistCoordinates = createAsyncThunk(
  "getBuyWishlistCoordinates",
  async (data, { rejectWithValue }) => {
    const URL = "/wishlist/coordinates";
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getRentWishlistCoordinates = createAsyncThunk(
  "getRentWishlistCoordinates",
  async (data, { rejectWithValue }) => {
    const URL = "/wishlist/coordinates";
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const setViewPropertyCount = createAsyncThunk(
  "setViewPropertyCount",
  async (data, { rejectWithValue }) => {
    const propertyId = data?.propertyId;
    delete data?.propertyId;
    const URL = `/${propertyId}/view`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getBuyAndRentPropertiesList = createAsyncThunk(
  "getBuyAndRentPropertiesList",
  async (data, { rejectWithValue }) => {
    const URL = `/search/buy-rent?pageIndex=${1}&pageSize=${25}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getBuyAndRentPropertiesListForGlobal = createAsyncThunk(
  "getBuyAndRentPropertiesListForGlobal",
  async (data, { rejectWithValue }) => {
    const URL = `/search/buy-rent?pageIndex=${1}&pageSize=${25}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getPropertiesList = createAsyncThunk(
  "getPropertiesList",
  async (data, { rejectWithValue }) => {
    const URL = `/search/list?pageIndex=${1}&pageSize=${100}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getPropertiesListForGlobal = createAsyncThunk(
  "getPropertiesListForGlobal",
  async (data, { rejectWithValue }) => {
    const URL = `/search/list?pageIndex=${1}&pageSize=${25}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getAllPropertiesList = createAsyncThunk(
  "getPropertiesList",
  async (_, { rejectWithValue }) => {
    const URL = `/search/allPropertysData?pageIndex=${1}&pageSize=${25}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.get(URL, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const universalSearch = createAsyncThunk(
  "universalSearch",
  async (data, { rejectWithValue }) => {
    const URL = `/search/global`;
    const token = JSON.parse(localStorage.getItem("token"));

    try {
      const response = await AxiosPropertyInstance.post(URL, data);
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getNonListedPropertiesList = createAsyncThunk(
  "getNonListedPropertiesList",
  async (data, { rejectWithValue }) => {
    const URL = `/search/addToListing?pageIndex=${1}&pageSize=${25}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getListedPropertiesList = createAsyncThunk(
  "getListedPropertiesList",
  async (data, { rejectWithValue }) => {
    const URL = `/search/addToListing?pageIndex=${1}&pageSize=${25}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const addWishlist = createAsyncThunk(
  "addWishlist",
  async (data, { rejectWithValue }) => {
    const URL = `/wishlist/add`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const removeWishlist = createAsyncThunk(
  "removeWishlist",
  async (data, { rejectWithValue }) => {
    const URL = `/wishlist/removeWishList?propertyId=${data?.propertyId}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.delete(URL, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

// export const getUserWishlist = createAsyncThunk(
//   "getUserWishlist",
//   async (data, { rejectWithValue }) => {
//     const URL = `/search/list?pageIndex=${1}&pageSize=${25}`;
//     const token = JSON.parse(localStorage.getItem("token"));
//     try {
//       const response = await AxiosPropertyInstance.delete(URL, data, {
//         headers: { "x-auth-token": token },
//       });
//       return { response: JSON.parse(JSON.stringify(response)) };
//     } catch (error) {
//       return rejectWithValue(JSON.parse(JSON.stringify(error)));
//     }
//   }
// );

export const getBuyWishList = createAsyncThunk(
  "getBuyWishList",
  async (wishlistBody, { rejectWithValue }) => {
    const URL = `/wishlist/userWishList`;
    const token = JSON.parse(localStorage.getItem("token"));

    try {
      const response = await AxiosPropertyInstance.post(URL, wishlistBody, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getRentWishList = createAsyncThunk(
  "getRentWishList",
  async (wishlistBody, { rejectWithValue }) => {
    const URL = `/wishlist/userWishList`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, wishlistBody, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const verifyPropertyDocs = createAsyncThunk(
  "verifyPropertyDocs",
  async (data, { rejectWithValue }) => {
    const URL = `/${data?.propertyId}/document/${data?.documentId}/verify`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(
        URL,
        {},
        {
          headers: { "x-auth-token": token },
        }
      );

      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getPropertyMasterData = createAsyncThunk(
  "getPropertyMasterData",
  async (data, { rejectWithValue }) => {
    // https://dev.jaaga.ai/api/property/static/states/telangana/non-agriculture/property/info/apartment.json
    const URL = `/static/states/${data.state}/${data.propertyType}/property/info/${data.categoryType}.json`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.get(URL, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getDocDataofProperty = createAsyncThunk(
  "getDocDataofProperty",
  async (data, { rejectWithValue }) => {
    const URL = `/static/states/${data.state}/${data.propertyType}/property/docs.json`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.get(URL, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getRiskDataOfProperty = createAsyncThunk(
  "getRiskDataOfProperty",
  async (data, { rejectWithValue }) => {
    const URL = `/static/states/${data.state}/${data.propertyType}/property/risk.json`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.get(URL, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const uploadProppertyDoc = createAsyncThunk(
  "uploadProppertyDoc",
  async ({ propertyId, documentId, data }, { rejectWithValue }) => {
    const URL = `/${propertyId}/docs/${documentId}`;

    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.put(URL, data, {
        headers: { "x-auth-token": token },
      });

      return {
        propertyId: propertyId,
        response: JSON.parse(JSON.stringify(response)),
      };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );
      return rejectWithValue({ ...returnedError });
    }
  }
);

export const sharePropertyDocument = createAsyncThunk(
  "sharePropertyDocument",
  async ({ propertyId, documentId, fileID }, { rejectWithValue }) => {
    const URL = `/${propertyId}/docs/${documentId}/file/${fileID}/share`;

    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.get(URL, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );
      return rejectWithValue({ ...returnedError });
    }
  }
);

// deletePropertyDoccument
export const deletePropertyDoccument = createAsyncThunk(
  "deletePropertyDoccument",
  async ({ propertyId, documentId, fileID }, { rejectWithValue }) => {
    const URL = `/${propertyId}/docs/${documentId}/file/${fileID}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.delete(URL, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );
      return rejectWithValue({ ...returnedError });
    }
  }
);

export const VerifyRisk = createAsyncThunk(
  "VerifyRisk",
  async (data, { rejectWithValue }) => {
    const URL = `/${data._id}/risk/notify`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data.body, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );
      return rejectWithValue({ ...returnedError });
    }
  }
);

export const SubscribeService = createAsyncThunk(
  "SubscribeService",
  async (data, { rejectWithValue }) => {
    const URL = `/${data._id}/risk/subscribe`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.post(URL, data.body, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      rejectWithValue(error);
    }
  }
);

export const buyAndRentReference = createAsyncThunk(
  "buyAndRentReference",
  async (data, { rejectWithValue }) => {
    const URL = `static/rent-sell/${data?.category}/${data?.subCategory}/index.json`;
    const token = JSON.parse(localStorage.getItem("token"));

    try {
      const response = await AxiosPropertyInstance.get(URL);

      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );
      return rejectWithValue({ ...returnedError });
    }
  }
);

// https://dev.jaaga.ai/api/property/static/jaaga-property/residential/apartment/index.json

export const getMasterApiReference = createAsyncThunk(
  "getMasterApiReference",
  async (_, { rejectWithValue }) => {
    const URL = `static/jaaga-property/residential/apartment/index.json`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await AxiosPropertyInstance.get(URL);
      return { response: JSON.parse(JSON.stringify(response)) };
    } catch (error) {
      rejectWithValue(error);
    }
  }
);
